import { useState, useMemo } from 'react';
import { loadFeatures } from '../lib/featureStore';
import PortalHeader from '../components/PortalHeader';
import PageHeader from '../components/PageHeader';

const IMG_BASE = 'https://racemarks.app/assets/img/screenshots/';
const CACHE_BUSTER = '?v=14';

function GatingBadge({ gating }) {
  if (gating === 'ui') return <span className="fm-badge fm-badge-ui">UI Gated</span>;
  if (gating === 'compile') return <span className="fm-badge fm-badge-compile">Compile Only</span>;
  return <span className="fm-badge fm-badge-none">No Gate</span>;
}

function BuildBadge({ label, enabled }) {
  const cls = enabled
    ? `fm-badge fm-badge-${label.toLowerCase()}`
    : 'fm-badge fm-badge-off';
  return <span className={cls}>{label}</span>;
}

function Badges({ item }) {
  return (
    <div className="fm-badges">
      <GatingBadge gating={item.gating} />
      <BuildBadge label="INT" enabled={item.build.internal} />
      <BuildBadge label="BETA" enabled={item.build.beta} />
      <BuildBadge label="PUB" enabled={item.build.public} />
    </div>
  );
}

function ScreenCard({ cap, theme }) {
  return (
    <div className="fm-screen-card">
      <img
        className="fm-screen-thumb"
        src={`${IMG_BASE}${cap.screenshot}_${theme}.png${CACHE_BUSTER}`}
        alt={cap.label}
        loading="lazy"
      />
      <div className="fm-screen-body">
        <h4>{cap.label}</h4>
        {cap.flag && <div className="fm-cap-meta"><code>{cap.flag}</code></div>}
        <Badges item={cap} />
      </div>
    </div>
  );
}

function ComponentCard({ item, theme }) {
  return (
    <div className="fm-component-card">
      <img
        className="fm-component-thumb"
        src={`${IMG_BASE}${item.screenshot}_${theme}.png${CACHE_BUSTER}`}
        alt={item.label}
        loading="lazy"
      />
      <div className="fm-component-body">
        <h4>{item.label}</h4>
        {item.flag && <div className="fm-cap-meta"><code>{item.flag}</code></div>}
        <Badges item={item} />
      </div>
    </div>
  );
}

function NoScreenshotChip({ cap }) {
  return (
    <div className="fm-chip">
      <div>
        <h4>{cap.label}</h4>
        {cap.flag && <code>{cap.flag}</code>}
      </div>
      <Badges item={cap} />
    </div>
  );
}

const TIER_COLORS = {
  free: '#2e7d32',
  season_pass: '#1565c0',
  navigator: '#e65100',
  tactician: '#b71c1c',
};

export default function FeatureMatrix() {
  const [theme, setTheme] = useState('light');
  const data = useMemo(() => loadFeatures(), []);

  return (
    <>
      <PortalHeader />
      <div className="page">
        <PageHeader backTo="/admin" backLabel="Admin" title="Feature Matrix" />

        <div className="fm-controls">
        <span>Screenshots:</span>
        <button
          className={`btn btn-sm${theme === 'light' ? ' btn-active' : ''}`}
          onClick={() => setTheme('light')}
        >
          Light
        </button>
        <button
          className={`btn btn-sm${theme === 'dark' ? ' btn-active' : ''}`}
          onClick={() => setTheme('dark')}
        >
          Dark
        </button>
      </div>

      <div className="fm-summary">
        <span className="fm-stat">
          <span className="fm-badge fm-badge-ui">UI Gated</span> {data.counts.ui}/{data.total}
        </span>
        <span className="fm-stat">
          <span className="fm-badge fm-badge-compile">Compile Only</span> {data.counts.compile}/{data.total}
        </span>
        <span className="fm-stat">
          <span className="fm-badge fm-badge-none">No Gate</span> {data.counts.none}/{data.total}
        </span>
      </div>

      {data.tiers.map(({ tier, label, screens, components, noScreenshot }) => (
        <div key={tier} className="fm-tier-group">
          <h3 style={{ color: TIER_COLORS[tier], borderColor: TIER_COLORS[tier] }}>
            {label}
          </h3>

          {screens.length > 0 && (
            <>
              <div className="fm-section-label">Screens</div>
              <div className="fm-screen-strip">
                {screens.map((cap) => (
                  <ScreenCard key={cap.name || cap.screenshot} cap={cap} theme={theme} />
                ))}
              </div>
            </>
          )}

          {components.length > 0 && (
            <>
              <div className="fm-section-label">Components</div>
              <div className="fm-component-grid">
                {components.map((item) => (
                  <ComponentCard key={item.name || item.screenshot} item={item} theme={theme} />
                ))}
              </div>
            </>
          )}

          {noScreenshot.length > 0 && (
            <div className="fm-chip-list">
              {noScreenshot.map((cap) => (
                <NoScreenshotChip key={cap.name || cap.flag} cap={cap} />
              ))}
            </div>
          )}
        </div>
      ))}
      </div>
    </>
  );
}
